import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./starRatings.html";
/*
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
const MAXIMUM_NUMBER_OF_STARS = 15;
const ALLOWED_ACTION_KEYS = ['Minus', 'ArrowDown', 'ArrowLeft', 'Plus', 'ArrowRight', 'ArrowUp', 'Backspace', 'Delete', 'Digit0'];
const STAR_COLOR = '#FFB75D';
const STAR_COLOR_EMPTY = '#DDDBDA';
export let StarSize = /*#__PURE__*/function (StarSize) {
  StarSize["XSSmall"] = "xsmall";
  StarSize["Small"] = "small";
  StarSize["Medium"] = "medium";
  StarSize["Large"] = "large";
  StarSize["XLarge"] = "xlarge";
  StarSize["XXLarge"] = "xxlarge";
  return StarSize;
}({});
export let StarsDirection = /*#__PURE__*/function (StarsDirection) {
  StarsDirection["Undefined"] = "";
  StarsDirection["LefToRight"] = "ltr";
  StarsDirection["RightToLeft"] = "rtl";
  return StarsDirection;
}({});
export let LabelPosition = /*#__PURE__*/function (LabelPosition) {
  LabelPosition["Top"] = "top";
  LabelPosition["Left"] = "left";
  LabelPosition["Right"] = "right";
  LabelPosition["Bottom"] = "bottom";
  return LabelPosition;
}({});
export let StarRatingPrecision = /*#__PURE__*/function (StarRatingPrecision) {
  StarRatingPrecision[StarRatingPrecision["QUARTER"] = 0.25] = "QUARTER";
  StarRatingPrecision[StarRatingPrecision["HALF"] = 0.5] = "HALF";
  StarRatingPrecision[StarRatingPrecision["FULL"] = 1] = "FULL";
  return StarRatingPrecision;
}({});
class StarRating extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labelPosition = LabelPosition.Left;
    this.size = StarSize.Medium;
    this.direction = StarsDirection.Undefined;
    this.disabled = false;
    this.spaceBetween = 'small';
    this.forceShowError = false;
    this.ariaLabel = null;
    this.required = null;
    this.reviewCount = null;
    this.ratingLabel = null;
    this.value = 0;
    this.precision = StarRatingPrecision.HALF;
    this.readOnly = false;
    // 'rating' serves as an interim value within the component,
    // 'rating' may not always be the same as 'value,' depending on the precision, it could take different values.
    this.rating = 0;
    this._numberOfStars = 5;
    // This function calculates the initial rating value and updates it when certain @api properties change.
    // It is crucial to update the new roundedRating whenever properties related to the rating calculation (value, precision, readOnly) change.
    this.calculateRoundedRating = () => {
      // We use this comparison instead of directly using the @api 'precision'
      // because precision other than FULL is only available in readOnly mode.
      const precision = this.readOnly ? this.precision : StarRatingPrecision.FULL;
      this.rating = Math.round(this.value / precision) * precision;
    };
    this.isDigitKeyEventCode = function (eventCode) {
      return eventCode.indexOf('Digit') === 0;
    };
    this.handleDigits = eventCode => {
      const dStr = 'Digit';
      const digitPressed = parseInt(eventCode.substr(dStr.length, eventCode.length - 1), 10);
      this.setRating(digitPressed);
      this.changeInputFocus(digitPressed);
    };
    this.getKeyHandler = keyEventCode => {
      const keyHandlers = {
        Minus: this.decrement,
        Plus: this.increment,
        ArrowDown: this.increment,
        ArrowRight: this.increment,
        ArrowUp: this.decrement,
        ArrowLeft: this.decrement,
        Backspace: this.reset,
        Delete: this.reset,
        Digit0: this.reset
      };
      keyHandlers[keyEventCode]?.();
    };
    this.increment = () => {
      const newRating = this.rating + 1;
      this.setRating(newRating > this.numberOfStars ? 1 : newRating);
      this.changeInputFocus(this.rating);
    };
    this.decrement = () => {
      const newRating = this.rating - 1;
      this.setRating(newRating <= 0 ? this.numberOfStars : newRating);
      this.changeInputFocus(this.rating);
    };
    this.reset = () => {
      this.setRating(0);
      this.changeInputFocus(1);
    };
    this.changeInputFocus = dataRating => {
      let elementToFocus = null;
      if (dataRating <= 0) {
        elementToFocus = this.template.querySelector(`input[data-rating="${this.numberOfStars}"]`);
      }
      if (dataRating > this.numberOfStars) {
        elementToFocus = this.template.querySelector(`input[data-rating="1"]`);
      }
      if (dataRating > 0 && dataRating <= this.numberOfStars) {
        elementToFocus = this.template.querySelector(`input[data-rating="${dataRating}"]`);
      }
      if (elementToFocus) {
        elementToFocus.focus();
      } else {
        throwDevelopmentOrLog(new Error(`Failed to find input with '${dataRating}'`));
      }
    };
  }
  i18nStoreCallback(i18n) {
    this.labels = i18n.starRatings;
  }
  set numberOfStars(numberOfStars) {
    const maxNumberAllowed = MAXIMUM_NUMBER_OF_STARS;
    this._numberOfStars = numberOfStars >= maxNumberAllowed ? maxNumberAllowed : numberOfStars;
  }
  get numberOfStars() {
    return this._numberOfStars;
  }
  connectedCallback() {
    this.calculateRoundedRating();
  }
  get isSingleReview() {
    return this.reviewCount === null;
  }
  get showNoReviewsText() {
    return this.readOnly && !this.value && !this.reviewCount;
  }
  get noReviewsText() {
    return this.labels.noReviews;
  }
  get containerRole() {
    return this.readOnly ? 'img' : 'radiogroup';
  }
  get spaceBetweenClass() {
    return 'space-' + this.spaceBetween;
  }
  get ratingAsInteger() {
    return Math.floor(this.rating);
  }
  get starsTabIndex() {
    return this.readOnly ? '-1' : '0';
  }
  get componentClassNames() {
    return ['rating', this.rating ? `rating-${this.ratingAsInteger}` : 'rating-0', this.useFractionalPrecision ? 'with-precision' : '', this.size, this.readOnly ? 'read-only' : '', this.disabled ? 'disabled' : '', `label-${this.labelPosition}`, this.direction ? `direction-${this.direction}` : '', this.spaceBetweenClass].join(' ');
  }
  get starsArray() {
    const stars = [];
    for (let i = 0; i < this.numberOfStars; i++) {
      const star = {
        id: 'star-' + (i + 1),
        value: i + 1,
        checked: i + 1 === this.value ? true : false,
        class: 'star value-filled',
        ariaLabel: i18nStore.getState().i18n.starRatings.ariaLabelStar({
          CURRENT_STAR: i + 1,
          NUMBER_OF_STARS: this.numberOfStars
        }),
        gradient: {
          color: STAR_COLOR,
          percent: '100%',
          empty: STAR_COLOR_EMPTY
        }
      };
      if (i >= this.rating) {
        star.class = 'star value-empty';
        star.gradient.percent = '0%';
      } else if (this.useFractionalPrecision && i === this.ratingAsInteger) {
        const gradientPercent = this.calculatePercentageOfLastStarToFill();
        star.class = `star value-with-precision`;
        star.gradient.percent = gradientPercent;
      }
      stars.push(star);
    }
    return stars;
  }
  get useFractionalPrecision() {
    return this.readOnly && this.precision !== StarRatingPrecision.FULL;
  }
  get ariaLabelText() {
    return this.ariaLabel || i18nStore.getState().i18n.starRatings.ariaLabelStar({
      CURRENT_STAR: this.value,
      NUMBER_OF_STARS: this.numberOfStars
    });
  }
  calculatePercentageOfLastStarToFill() {
    return (this.rating % 1 * 100).toFixed(0) + '%';
  }
  onStarClicked(event) {
    if (!this.interactionPossible()) {
      return;
    }

    // Get the HTML element that was clicked
    const target = event.target;
    if (!target) {
      return;
    }

    // Get the closest radio button element to the clicked element
    const starRadio = target.closest('[data-rating]');
    const ratingValue = starRadio ? parseInt(starRadio.getAttribute('data-rating'), 10) : 0;
    this.setRating(ratingValue);
  }

  // This method is triggered whenever the rating value changes through user interactions with the input components.
  // It handles actions like clicks or keyboard accessibility actions to update the rating value accordingly.
  setRating(rating) {
    let newRating = rating;
    // Ensure rating is within the valid range
    if (rating < 0) {
      newRating = 0;
    }
    if (rating > this.numberOfStars) {
      newRating = this.numberOfStars;
    }
    this.rating = newRating;
    this.template.querySelector('.star-container')?.setAttribute('aria-activedescendant', `star-${newRating}`);

    // Dispatches a 'change' event after every rating value change to notify listeners about updates in the rating value.
    this.dispatchEvent(new CustomEvent('change', {
      detail: {
        rating: this.rating
      }
    }));
  }
  interactionPossible() {
    return !this.readOnly && !this.disabled;
  }
  onKeyDown(event) {
    if (!this.interactionPossible()) {
      return;
    }
    const code = event.code;
    const isAllowedKey = ALLOWED_ACTION_KEYS.includes(code);
    const isDigitKeyEvent = this.isDigitKeyEventCode(code);
    if (isAllowedKey || isDigitKeyEvent) {
      if (isDigitKeyEvent) {
        this.handleDigits(code);
      } else {
        this.getKeyHandler(code);
      }
      event.preventDefault();
      event.stopPropagation();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(StarRating, {
  publicProps: {
    labelPosition: {
      config: 0
    },
    size: {
      config: 0
    },
    direction: {
      config: 0
    },
    disabled: {
      config: 0
    },
    spaceBetween: {
      config: 0
    },
    forceShowError: {
      config: 0
    },
    ariaLabel: {
      config: 0
    },
    required: {
      config: 0
    },
    reviewCount: {
      config: 0
    },
    ratingLabel: {
      config: 0
    },
    value: {
      config: 0
    },
    precision: {
      config: 0
    },
    readOnly: {
      config: 0
    },
    numberOfStars: {
      config: 3
    }
  },
  wire: {
    i18nStoreCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["rating", "_numberOfStars", "calculateRoundedRating", "isDigitKeyEventCode", "handleDigits", "getKeyHandler", "increment", "decrement", "reset", "changeInputFocus"]
});
const __lwc_component_class_internal = _registerComponent(StarRating, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;